import Link from 'next/link';
import {
  isValidElement,
  type KeyboardEventHandler,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { CaretDown, X } from '@/components/icons';
import type { Level } from '@/domain/constants';
import styles from './chip.module.css';

/**
 * The one chip (docs/DESIGN_SYSTEM.md "Chip", ruled 2026-10-20): every chip in
 * the app is this component, so neighbours of one size are one height by
 * construction. Five things vary, each a prop: the size, the tone, the look,
 * the slots around the label, and what the chip does when pressed.
 *
 * - `size`: `sm` (18px: rows, cards, entries, meta) or `md` (24px, 32px on a
 *   coarse pointer: the control lines - the state line, the criteria bar,
 *   fields, pickers, heads). The heights are tokens (`--chip-h-*`).
 * - `tone`: the mark (fill, edge, dot) and the ink (label), from the token
 *   layers only. `chipToneOf` maps the levels.
 * - `look`: `tint` is a value someone or the system declared (health, tags,
 *   field values, people, seat words); `outline` is a state word or a control
 *   value on a line that edits it; `dashed` is an inference or an offer.
 * - Slots, in order: `lead` (a dot whose shape carries the grade, an icon, or
 *   an avatar), the label, `trail` (anything, or an avatar), the caret, the
 *   remove X.
 * - Mode: a static span, a button (`onClick`, optionally the trigger of a
 *   `popup` or a disclosure that is `expanded`), or a link (`href`). Each may
 *   add `onRemove` (md only): the root is then a span holding the body and
 *   the X as siblings, because a button cannot hold a button. `pending` and
 *   `disabled` turn off every press the chip has, the X's included.
 *
 * No `className` and no `style`: a consumer cannot bend a metric, so layout
 * comes from the parent's gap and wrap, and `shrink` is the one layout prop
 * (the guard: eslint.config.mjs and scripts/check-chip-css.ts).
 *
 * The X keeps the label optically centred: a text end starts (h - ink) / 2
 * from the outer edge, and the X is a square as tall as the chip, flush with
 * its end, whose glyph ink sits (h - ink) / 2 inside it (the tag chip ruling
 * of 2026-10-19, at every size). Server-safe: no hooks.
 */

export type ChipSize = 'sm' | 'md';

/** The mark and the ink, from the token layers: base (`neutral`, `dim`),
 *  signals (`good`, `warning`, `critical`, the two reds for the last) and
 *  brand (`accent`). */
export type ChipTone = 'neutral' | 'dim' | 'good' | 'warning' | 'critical' | 'accent';

export type ChipLook = 'tint' | 'outline' | 'dashed';

/** What leads the label: a dot whose SHAPE carries the grade (filled for
 *  critical, a ring for warning: the cue that is not colour), a sign, or a
 *  person (a 16px avatar, seated concentric with the end cap). */
export type ChipLead = { dot: 'filled' | 'ring' } | { icon: ReactNode } | { avatar: ReactNode };

/** What follows the label: anything (a finding sign, a flow sign, a key hint),
 *  or a person (a 16px avatar), seated concentric with the end cap when
 *  nothing follows it. */
export type ChipTrail = ReactNode | { avatar: ReactNode };

/** The levels as chip tones: `info` is the quiet neutral, `notice`
 *  the dim grey, and the two loud grades keep tones of their own. */
export function chipToneOf(level: Level): ChipTone {
  switch (level) {
    case 'info':
      return 'neutral';
    case 'notice':
      return 'dim';
    case 'warning':
      return 'warning';
    case 'critical':
      return 'critical';
  }
}

/** A popup the button opens: `aria-haspopup` (a menu unless it says dialog)
 *  and `aria-expanded`. The chip is then the kit `Menu`'s trigger, or the
 *  opener of an editor. */
export interface ChipPopup {
  open: boolean;
  kind?: 'menu' | 'dialog';
}

interface ChipBase {
  /** Default `sm`. */
  size?: ChipSize;
  /** Default `neutral`. */
  tone?: ChipTone;
  /** Default `tint`. */
  look?: ChipLook;
  lead?: ChipLead;
  /** After the label: a finding sign, a flow sign, or `{ avatar }` (the
   *  declarer's face). */
  trail?: ChipTrail;
  /** May give way in a row, the label ending in an ellipsis. A chip does not
   *  shrink by default (`flex: none`). */
  shrink?: boolean;
  /** The tooltip. On a chip with an X it lands on the body, so it never
   *  shows over the X, which has its own name. */
  title?: string;
  /** On the root. */
  testId?: string;
}

type ChipLabel =
  | {
      /** The label. Rich children are fine (a criterion's parts). */
      children: ReactNode;
      iconOnly?: false;
      /** The name when the label is split into parts that a computed name
       *  would space apart. */
      ariaLabel?: string;
    }
  | {
      /** The icon: the chip is a circle as tall as the chips beside it. */
      children: ReactNode;
      iconOnly: true;
      ariaLabel: string;
    };

interface ChipStatic {
  onClick?: never;
  href?: never;
  popup?: never;
  expanded?: never;
  caret?: never;
  bodyRef?: never;
  onKeyDown?: never;
  tabIndex?: never;
}

type ChipButton = {
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  href?: never;
  /** The caret cue ("this opens"), drawn on a fine pointer only. */
  caret?: boolean;
  /** Arrow-key roving (the criteria bar): the ref, `tabIndex` and `onKeyDown`
   *  land on the button (the body, when the chip has an X). */
  bodyRef?: Ref<HTMLButtonElement>;
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
  tabIndex?: number;
} & (
  | { popup?: ChipPopup; expanded?: never }
  | {
      /** A disclosure: the press shows or hides something inline (ChipInput's
       *  description editor), so `aria-expanded` and the open look, with no
       *  `aria-haspopup`. */
      expanded?: boolean;
      popup?: never;
    }
);

interface ChipLink {
  /** An item link comes from src/lib/trail.ts, so "how I got here" survives. */
  href: string;
  onClick?: never;
  popup?: never;
  expanded?: never;
  caret?: never;
  bodyRef?: Ref<HTMLAnchorElement>;
  onKeyDown?: KeyboardEventHandler<HTMLAnchorElement>;
  tabIndex?: number;
}

/** What turns a press off, on a chip that has one (a button, or an X). */
interface ChipPressable {
  /** A write in flight: every press in the chip (the button, the X) is
   *  disabled at once and the chip is `aria-busy`; it dims only after 400ms,
   *  so a fast round trip does not flash (the state line's rule, 2026-09-16). */
  pending?: boolean;
  /** Off: every press in the chip is disabled and the chip dims at once, like
   *  every kit control. */
  disabled?: boolean;
}

interface ChipUnpressable {
  pending?: never;
  disabled?: never;
}

interface ChipKept {
  onRemove?: never;
  removeLabel?: never;
  removeTestId?: never;
  bodyTestId?: never;
}

interface ChipRemovable {
  /** The X is md only: an 18px square is below any touch target. */
  size: 'md';
  onRemove: () => void;
  /** The X's accessible name: the surface's own catalog sentence. */
  removeLabel: string;
  removeTestId?: string;
  /** On the body (the label's button, link or span) beside the X. */
  bodyTestId?: string;
}

/** The mode and the X. A press can be pending or disabled only where the chip
 *  has one: a button, or an X. */
type ChipMode =
  | ((ChipStatic | ChipLink) & ChipKept & ChipUnpressable)
  | (ChipButton & ChipKept & ChipPressable)
  | ((ChipStatic | ChipButton | ChipLink) & ChipRemovable & ChipPressable);

/** `data-*` attributes pass through to the root. */
type ChipData = { [key: `data-${string}`]: string | number | boolean | undefined };

export type ChipProps = ChipBase & ChipLabel & ChipMode & ChipData;

type Attrs = {
  className?: string;
  title?: string;
  'aria-label'?: string;
  'aria-busy'?: boolean;
} & ChipData;

type DataValue = ChipData[`data-${string}`];

function dataAttributes(props: object): ChipData {
  const out: ChipData = {};
  for (const [key, value] of Object.entries(props)) {
    if (key.startsWith('data-')) out[key as `data-${string}`] = value as DataValue;
  }
  return out;
}

/** Is the trail a person (`{ avatar }`) rather than a node? */
function avatarTrail(trail: ChipTrail): trail is { avatar: ReactNode } {
  return typeof trail === 'object' && trail !== null && !isValidElement(trail) && 'avatar' in trail;
}

function Lead({ lead }: { lead: ChipLead }) {
  if ('dot' in lead) {
    return (
      <span className={styles.lead} aria-hidden>
        <span className={styles.dot} data-shape={lead.dot} />
      </span>
    );
  }
  if ('avatar' in lead) {
    return (
      <span className={styles.lead} data-round>
        {lead.avatar}
      </span>
    );
  }
  return (
    <span className={styles.lead} aria-hidden>
      {lead.icon}
    </span>
  );
}

export function Chip(props: ChipProps) {
  const {
    size = 'sm',
    tone = 'neutral',
    look = 'tint',
    lead,
    trail,
    shrink,
    title,
    testId,
    children,
    iconOnly,
    ariaLabel,
    onClick,
    href,
    popup,
    expanded,
    caret,
    pending,
    disabled,
    bodyRef,
    onKeyDown,
    tabIndex,
    onRemove,
    removeLabel,
    removeTestId,
    bodyTestId,
  } = props;

  const content = iconOnly ? (
    children
  ) : (
    <>
      {lead && <Lead lead={lead} />}
      <span className={styles.label}>{children}</span>
      {trail != null &&
        (avatarTrail(trail) ? (
          <span className={styles.trail} data-round>
            {trail.avatar}
          </span>
        ) : (
          <span className={styles.trail}>{trail}</span>
        ))}
      {caret && <CaretDown className={styles.caret} aria-hidden />}
    </>
  );

  // Every press the chip has (the button, the X) is off while pending or
  // disabled.
  const off = pending || disabled || undefined;

  // The element that carries the mode: the root itself, or the body beside
  // the X when the chip has one.
  const control = (attrs: Attrs) => {
    if (onClick) {
      return (
        <button
          {...attrs}
          ref={bodyRef as Ref<HTMLButtonElement> | undefined}
          type="button"
          aria-haspopup={popup ? (popup.kind ?? 'menu') : undefined}
          aria-expanded={popup ? popup.open : expanded}
          disabled={off}
          tabIndex={tabIndex}
          onClick={onClick}
          onKeyDown={onKeyDown as KeyboardEventHandler<HTMLButtonElement> | undefined}
        >
          {content}
        </button>
      );
    }
    if (href !== undefined) {
      return (
        <Link
          {...attrs}
          ref={bodyRef as Ref<HTMLAnchorElement> | undefined}
          href={href}
          tabIndex={tabIndex}
          onKeyDown={onKeyDown as KeyboardEventHandler<HTMLAnchorElement> | undefined}
        >
          {content}
        </Link>
      );
    }
    return <span {...attrs}>{content}</span>;
  };

  const root: Attrs = {
    className: styles.chip,
    'aria-busy': pending || undefined,
    'data-size': size,
    'data-tone': tone,
    'data-look': look,
    'data-icon-only': iconOnly || undefined,
    'data-shrink': shrink || undefined,
    'data-pending': pending || undefined,
    'data-disabled': disabled || undefined,
    'data-testid': testId,
    ...dataAttributes(props),
  };

  if (!onRemove) return control({ ...root, title, 'aria-label': ariaLabel });

  return (
    <span {...root} data-removable>
      {control({
        className: styles.body,
        title,
        'aria-label': ariaLabel,
        'data-testid': bodyTestId,
      })}
      <button
        type="button"
        className={styles.remove}
        aria-label={removeLabel}
        disabled={off}
        onClick={onRemove}
        data-testid={removeTestId}
      >
        <X aria-hidden />
      </button>
    </span>
  );
}
